import React from "react";
import _ from "lodash";
import { connect } from "react-redux";
import { injectIntl } from "react-intl";
import { Action } from "typescript-fsa";
import { Bar } from "react-chartjs-2";
import { ChartData, ChartOptions, TooltipItem, Tick } from "chart.js";

import { makeDemographicsDownloadGet } from "@api/index";
import { DownloadCsvButton } from "@ui/DownloadCsvButton";
import { compose } from "@util/compose";
import { getChartTooltipOptions } from "../../constants";
import {
    AlbumTrackVendorProps,
    makeGetAgeChartsData,
    getSettingFrom,
    getSettingTo,
    IntlProps,
} from "../../selectors";
import { SalesState } from "../../models";

interface AgeBarChartOwnProps extends AlbumTrackVendorProps {
    download?: boolean;
}

interface AgeBarChartStateProps {
    data: ChartData<"bar">;
    downloadAction: () => Action<any>;
}

type Props = AgeBarChartOwnProps & AgeBarChartStateProps & IntlProps;

const AgeBarChart = ({ data, download, downloadAction, intl: { formatNumber } }: Props) => {
    const options: ChartOptions<"bar"> = {
        indexAxis: "y" as const,
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 1,
        scales: {
            x: {
                ticks: {
                    callback(value: number, index: number, values: Tick[]) {
                        if (index === values.length - 1) {
                            return null;
                        }
                        return formatNumber(value);
                    },
                },
                border: {
                    dash: [4, 3],
                },
            },
            y: {
                border: {
                    dash: [4, 3],
                },
            },

        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                ...getChartTooltipOptions(),
                callbacks: {
                    label: ({ dataset, dataIndex }: TooltipItem<"bar">) => {
                        let label = ": ";
                        // Format the number
                        let total = 0;
                        for (const gVal of dataset.data) {
                            total += gVal as number;
                        }
                        const val = Number(dataset.data[dataIndex]);
                        label += formatNumber(val);
                        // Add a percentage
                        label += " (" + formatNumber((val / total), {
                            style: "percent",
                            maximumFractionDigits: 2,
                        }) + ")";
                        return label;
                    },
                },
            },
        },
    };
    if (download == null) {
        download = true;
    }

    return (
        <div className="age-bar-chart">
            <div className="age-bar-chart__chart">
                <Bar data={data} options={options} height={null} width={null} />
            </div>
            {download && <DownloadCsvButton actionCreator={downloadAction} />}
        </div>
    );
};

const makeMapStateToProps = () => {
    const getAgeChartsData = makeGetAgeChartsData();
    return (state: SalesState, props: AlbumTrackVendorProps) => {
        const from = getSettingFrom(state);
        const to = getSettingTo(state);
        return {
            data: getAgeChartsData(state, props),
            downloadAction: makeDemographicsDownloadGet(
                props.albumId,
                props.trackId,
                props.artistId,
                from,
                to,
                "age",
            ),
        } as AgeBarChartStateProps;
    };
};

const ConnectedAgeBarChart = compose<Props, AgeBarChartOwnProps>(
    injectIntl,
    connect(makeMapStateToProps, null),
)(AgeBarChart);

export {
    AgeBarChart as AgeBarChartPresenter,
    ConnectedAgeBarChart as AgeBarChart,
};
